import React, { useState, useEffect } from 'react';
import { ReviewResponseFormProps, ReviewResponseData } from '../../types/review-types';

const MAX_RESPONSE_LENGTH = 500;

export const ReviewResponseForm: React.FC<ReviewResponseFormProps> = ({
  reviewId,
  deadline,
  onSubmit,
  onCancel,
  loading = false,
  error
}) => {
  const [responseText, setResponseText] = useState('');
  const [validationError, setValidationError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  // Calculate time remaining until deadline
  useEffect(() => {
    const updateTimeRemaining = () => {
      const now = new Date();
      const deadlineDate = new Date(deadline);
      const timeDiff = deadlineDate.getTime() - now.getTime();

      if (timeDiff <= 0) {
        setTimeRemaining('Expired');
        return;
      }

      const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setTimeRemaining(`${days} day${days !== 1 ? 's' : ''} remaining`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours} hour${hours !== 1 ? 's' : ''} remaining`);
      } else {
        setTimeRemaining(`${minutes} minute${minutes !== 1 ? 's' : ''} remaining`);
      }
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [deadline]);

  const validateResponse = (): boolean => {
    if (!responseText.trim()) {
      setValidationError('Please write a response');
      return false;
    }

    if (responseText.length > MAX_RESPONSE_LENGTH) {
      setValidationError(`Response must be ${MAX_RESPONSE_LENGTH} characters or less`);
      return false;
    }

    // Check if deadline has passed
    const now = new Date();
    const deadlineDate = new Date(deadline);
    if (now > deadlineDate) {
      setValidationError('The deadline to respond has passed');
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleTextChange = (text: string) => {
    setResponseText(text);
    
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateResponse()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const responseData: ReviewResponseData = {
        responseText: responseText.trim()
      };
      await onSubmit(responseData);
    } catch (err) {
      // Error handling is done by parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDeadlineExpired = () => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    return now > deadlineDate;
  };

  const getDeadlineColor = () => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const timeDiff = deadlineDate.getTime() - now.getTime();
    const hoursRemaining = timeDiff / (1000 * 60 * 60);

    if (hoursRemaining <= 0) return 'text-red-600';
    if (hoursRemaining <= 24) return 'text-orange-600';
    if (hoursRemaining <= 72) return 'text-yellow-600';
    return 'text-green-600';
  };

  const formatDeadline = (deadlineString: string): string => {
    const date = new Date(deadlineString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isDeadlineExpired()) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-2">
          <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="text-red-800 font-medium">Response Deadline Expired</span>
        </div>
        <p className="text-red-700 text-sm">
          The deadline to respond to this review was {formatDeadline(deadline)}. 
          You can no longer submit a response.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
      <div className="mb-4">
        <h4 className="text-lg font-medium text-gray-900 mb-2">Respond to Review</h4>
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Share your perspective or address any concerns mentioned in the review
          </p>
          <div className={`text-sm font-medium ${getDeadlineColor()}`}>
            {timeRemaining}
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Deadline: {formatDeadline(deadline)}
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="responseText" className="block text-sm font-medium text-gray-700 mb-2">
            Your Response
          </label>
          <textarea
            id="responseText"
            value={responseText}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder="Write a professional response to address the review. Keep it constructive and helpful..."
            rows={4}
            maxLength={MAX_RESPONSE_LENGTH}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${
              validationError ? 'border-red-300' : 'border-gray-300'
            }`}
            disabled={loading || isSubmitting}
          />
          <div className="flex justify-between items-center mt-2">
            <div>
              {validationError && (
                <p className="text-sm text-red-600">{validationError}</p>
              )}
            </div>
            <p className="text-sm text-gray-500">
              {responseText.length}/{MAX_RESPONSE_LENGTH} characters
            </p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="flex items-start space-x-2">
            <svg className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Response Guidelines:</p>
              <ul className="text-xs space-y-1">
                <li>• Keep your response professional and constructive</li>
                <li>• Address specific concerns mentioned in the review</li>
                <li>• Avoid being defensive; focus on solutions and improvements</li>
                <li>• Thank the reviewer for their feedback</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-150"
            disabled={loading || isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
            disabled={loading || isSubmitting || !responseText.trim()}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Submitting...</span>
              </div>
            ) : (
              'Submit Response'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewResponseForm;